import { useEffect, type RefObject } from 'react';

/**
 * Keeps the barcode field focused for a USB keyboard-wedge scanner.
 *
 * The Apps Script version did this with setInterval every second, which fought
 * the user for the caret and stole focus from fields it did not know about.
 * This listens for focus leaving instead, so it reacts immediately, costs
 * nothing while idle, and yields whenever a dialog or an allowed field is
 * active.
 */
export function useScanFocus(
  ref: RefObject<HTMLInputElement | null>,
  options: { enabled: boolean; pausedBy?: boolean },
): void {
  const { enabled, pausedBy = false } = options;

  useEffect(() => {
    if (!enabled || pausedBy) return;

    const input = ref.current;
    if (!input) return;

    input.focus();

    const reclaim = (): void => {
      const active = document.activeElement;

      // Leave the user alone while they are genuinely typing somewhere else.
      if (
        active instanceof HTMLElement &&
        (active.tagName === 'INPUT' ||
          active.tagName === 'TEXTAREA' ||
          active.tagName === 'SELECT' ||
          active.isContentEditable ||
          active.closest('[role="dialog"]'))
      ) {
        return;
      }
      ref.current?.focus();
    };

    // A frame's delay lets the browser settle focus before we judge it.
    const onFocusOut = (): void => {
      window.requestAnimationFrame(reclaim);
    };

    document.addEventListener('focusout', onFocusOut);
    return () => document.removeEventListener('focusout', onFocusOut);
  }, [ref, enabled, pausedBy]);
}
